/**
 * Haversine 공식으로 두 좌표 간 직선거리(km) 계산
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // 지구 반지름 (km)
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * 도로거리 추정 — 직선거리 티어별 보정 계수 적용
 * - 0 ~ 10km: × 1.3
 * - 10 ~ 25km: × 1.2
 * - 25km 초과: × 1.15
 *
 * 가격 계산용 surcharge 와 동일 티어 분해를 사용 (price-calculator.calcDistanceSurcharge).
 */
export async function getRoadDistance(
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number
): Promise<number> {
  const straightDistance = haversineDistance(originLat, originLng, destLat, destLng);
  return getRoadDistanceFromStraight(straightDistance);
}

export function getRoadDistanceFromStraight(straightKm: number): number {
  if (!Number.isFinite(straightKm) || straightKm <= 0) return 0;
  const tiers = [
    { maxStraightKm: 10, multiplier: 1.3 },
    { maxStraightKm: 25, multiplier: 1.2 },
    { maxStraightKm: Infinity, multiplier: 1.15 },
  ];
  let remaining = straightKm;
  let prevMax = 0;
  let roadKm = 0;
  for (const tier of tiers) {
    const used = Math.min(remaining, tier.maxStraightKm - prevMax);
    if (used <= 0) break;
    roadKm += used * tier.multiplier;
    remaining -= used;
    prevMax = tier.maxStraightKm;
    if (remaining <= 0) break;
  }
  return Math.round(roadKm * 10) / 10;
}
